import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useGlobalSettings } from '../hooks/useGlobalSettings';
import toast from 'react-hot-toast';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const { settings } = useGlobalSettings();

  useEffect(() => {
    if (!token) {
      toast.error('Link inválido. Solicite uma nova redefinição de senha.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error('Link inválido.');
      return;
    }
    if (senha.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres.');
      return;
    }
    if (senha !== confirmarSenha) {
      toast.error('As senhas não coincidem.');
      return;
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(senha)) {
      toast.error('Use letras maiúsculas, minúsculas e números.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, senha })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess(true);
        toast.success(data.message);
      } else {
        toast.error(data.message || 'Não foi possível redefinir a senha.');
      }
    } catch {
      toast.error('Erro de conexão. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <p className="text-gray-700 mb-4">Link inválido ou expirado.</p>
          <Link
            to="/esqueci-senha"
            className="inline-block py-2 px-4 rounded-xl font-medium text-white"
            style={{ background: '#008070' }}
          >
            Solicitar novo link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-1/2 p-12 flex items-center justify-center text-white" style={{ background: '#008070' }}>
            <div className="text-center">
              <div className="w-32 h-32 bg-white rounded-2xl mx-auto mb-8 flex items-center justify-center shadow-lg">
                {settings?.iconUrl ? (
                  <img src={settings.iconUrl} alt="Ícone" className="w-24 h-24 object-contain" />
                ) : (
                  <img src="/favicon.png" alt="AutoWhats" className="w-24 h-24 object-contain" />
                )}
              </div>
              <h1 className="text-4xl font-bold mb-4">AutoWhats 3.0</h1>
              <p className="text-xl text-gray-100 mb-8">Sistema de Gestão de Campanhas</p>
              <p className="text-gray-200 max-w-md">
                Defina uma nova senha para acessar sua conta.
              </p>
            </div>
          </div>

          <div className="lg:w-1/2 p-12">
            <div className="max-w-md mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Nova senha</h2>
                <p className="text-gray-600">
                  Digite e confirme sua nova senha. Use letras maiúsculas, minúsculas e números.
                </p>
              </div>

              {success ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm">
                    Senha alterada com sucesso. Faça login com a nova senha.
                  </div>
                  <Link
                    to="/login"
                    className="block w-full text-center py-3 px-6 rounded-xl font-medium text-white"
                    style={{ background: '#008070' }}
                  >
                    Ir para o login
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="senha" className="block text-sm font-medium text-gray-700 mb-2">
                      Nova senha
                    </label>
                    <input
                      type="password"
                      id="senha"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': '#008070' } as React.CSSProperties}
                      placeholder="Mínimo 6 caracteres"
                      disabled={isSubmitting}
                      required
                      minLength={6}
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmarSenha" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar nova senha
                    </label>
                    <input
                      type="password"
                      id="confirmarSenha"
                      value={confirmarSenha}
                      onChange={(e) => setConfirmarSenha(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': '#008070' } as React.CSSProperties}
                      placeholder="Repita a senha"
                      disabled={isSubmitting}
                      required
                      minLength={6}
                      autoComplete="new-password"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-white py-3 px-6 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: '#008070', '--tw-ring-color': '#008070' } as React.CSSProperties}
                  >
                    {isSubmitting ? 'Salvando...' : 'Redefinir senha'}
                  </button>

                  <p className="text-center">
                    <Link to="/login" className="text-sm font-medium hover:underline" style={{ color: '#008070' }}>
                      Voltar ao login
                    </Link>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
